import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'discosquares',
  family: 'grid',
  energyLevel: 0.70,
  bpmMin: 95,
  bpmMax: 165,
  movement: 0.60,
  // 0.80, not the 0.70 it launched with - same tier adjustment as
  // laser-grid.js/portal.js's own danceability comments: the pool picks
  // the profiles NEAREST the track's index, and the danceable tracks this
  // piece is for cluster around 0.80, where it now actually competes.
  danceability: 0.80,
  tonality: 0.75,
  enabled: true
};

// DiscoSquares: a full-screen grid of square tiles - a retro disco-
// floor/light-grid look, distinct from SpotWall (round dots, one flat
// color, independent per-spot random flicker) via square-not-round
// sprites and, since this version, a shared spatial on/off field instead
// of independent-per-tile randomness.
//
// Which tiles are lit is NOT decided per-tile anymore (an earlier version
// gave each tile its own random on/off timer, which read as isolated
// twinkling rather than "des formes floues synchronisees sur l'ecran" -
// blurry synchronized shapes). Instead every tile's brightness comes from
// sampling ONE continuous 2D field (see the vertex shader) at that tile's
// own world position - nearby tiles necessarily sample similar values, so
// lit tiles naturally cluster into soft moving blobs that drift/shift
// across the grid together, rather than flickering independently.
//
// Every tile's X/Y grid position is fixed for the visualizer's entire
// lifetime; only brightness and a subtle per-tile depth (Z) wave animate
// (see DEPTH_WAVE_* below) - so tiles can never drift off their grid
// cell or grow/shrink enough to overlap their neighbors. Each tile is a
// flat solid fill - an animated
// internal light/dark gradient was tried and dropped: even a subtle
// version of it read as a faint embossed/beveled 3D look, since any
// directional brightness gradient across a flat shape mimics a light
// source hitting a raised surface.
//
// Same 50/50 fixed-palette / real-album-color split as SpotWall (see
// spot-wall.js's _recomputeColors) - half the tiles always stay in the
// brief's own warm orange/pink/purple palette, half take one of the
// album's real vivid colors when available. Base palette uses 3 fixed
// hue anchors (not a continuous band) since "orange, pink, purple" are
// three distinct hues, not a contiguous slice of the color wheel.

// Wider spacing, fewer/bigger tiles, more visible gap between them - the
// full-screen coverage math below derives cols/rows from SQUARE_SPACING,
// so a bigger spacing means fewer tiles automatically.
const SQUARE_SPACING = 1.25;
const COVERAGE = 0.96;
const CAMERA_DISTANCE = 7.5;
const GRID_CENTER_Y = 2.0;
// Fixed at all times (position AND size - see the vertex shader) - ~45%
// of SQUARE_SPACING, comfortably under it so the wider gap from the
// spacing above actually reads as more space, not eaten back up by
// bigger tiles.
const POINT_WORLD_SIZE = 0.48;
const BASE_DIM = 0.05;
const INTENSITY_BOOST = 1.5;

// The shared on/off field (see the vertex shader) - three overlapping
// sine terms at different world-space frequencies/time-speeds/directions,
// summed and reshaped through a smoothstep threshold band. Several
// mismatched frequencies is what keeps the blobs organic-looking rather
// than a single obvious repeating ripple; the threshold band (FIELD_LOW/
// FIELD_HIGH) controls how much of the grid reads as "lit" at once versus
// how sharp the blob edges are.
const FIELD_FREQ_X = 0.9;
const FIELD_FREQ_Y = 0.9;
const FIELD_FREQ_DIAG = 0.5;
const FIELD_SPEED_X = 0.7;
const FIELD_SPEED_Y = 0.5;
const FIELD_SPEED_DIAG = 0.35;
const FIELD_LOW = 0.1;
const FIELD_HIGH = 0.55;

// Beat pulse (see beat-pulse.js) was tried here and removed - uIntensity
// is one uniform shared by every tile in the whole grid at once (a
// THREE.Points material can't vary it per-vertex), so any amount of beat
// pulse meant literally every currently-lit tile brightening in perfect
// unison on every beat - a structural whole-grid strobe, not just a
// tuning/amplitude problem.
//
// The beat is back, and it sidesteps that objection instead of arguing with
// it: it is SPATIAL. Every beat drops a front at one point on the grid which
// then sweeps outward across it (BEAT_WAVE_* below). A tile's response is a
// function of ITS OWN distance to that front, so tiles light in sequence,
// never in unison - a wave crossing the floor rather than a strobe. It still
// costs the grid exactly four scalar uniforms per frame and no per-tile
// state, because the front is one expanding radius, not a list of events.
//
// Tempo still matters here, just not as a flash: the field's own time
// rate (see update()) is scaled by BPM relative to REFERENCE_BPM - a
// faster track makes the blobs drift/reshape/flicker faster, a slower
// one makes them settle more calmly, with no synchronized brightness
// pulse anywhere. Clamped so a very slow or very fast track doesn't stall
// the field or make it flicker uncomfortably fast.
const REFERENCE_BPM = 120;
const TEMPO_MULTIPLIER_MIN = 0.6;
const TEMPO_MULTIPLIER_MAX = 1.8;

// Three fixed hue anchors - orange, hot pink, purple - rather than a
// continuous hue band, since the brief names three distinct colors, not
// a gradient between them.
const HUE_ANCHORS = [0.08, 0.92, 0.78]; // ~29deg orange, ~331deg pink, ~281deg purple
const HUE_JITTER = 0.02;
const SAT_MIN = 0.65;
const SAT_MAX = 0.95;
const LIGHT_MIN = 0.48;
const LIGHT_MAX = 0.68;
const VIVID_JITTER_AMOUNT = 0.3;

// Per-tile grain texture (see the fragment shader) - a per-pixel random
// sparkle rather than a smooth gradient, so it reads as a textured
// surface instead of risking the same directional-brightness "3D" bump
// look a smooth internal gradient caused earlier. Animated slowly so it
// shimmers rather than sitting static.
const TEXTURE_SCALE = 7.0; // grain frequency within each tile's own 0..1 sprite space
const TEXTURE_AMOUNT = 0.22; // +/- brightness swing per grain cell
const TEXTURE_SPEED = 0.35;

// Rather than moving the camera (owned/driven by scene-manager.js's own
// global drift, recomputed every frame from a fixed base captured once
// at init - anything this class set on the camera per-frame would just
// be overwritten immediately after), the grid object itself gets a slow
// glide instead. A first version rotated the grid (rotation.x/y) for
// this, which tilts the flat tile plane in 3D every frame - since the
// grid is viewed from a camera that isn't perfectly perpendicular to it,
// that tilt keystones the projected spacing (columns/rows compress
// unevenly toward one edge), which read as the tiles not quite lining up
// rather than as a camera glide. A pure translation (pan sideways, dolly
// in/out) can't warp a rigid grid's own internal spacing at all - only
// where the whole rigid shape sits/how big it reads - so it gives the
// same "the perspective is sliding" feel while every tile stays exactly
// on-grid relative to its neighbors.
const GRID_SWAY_AMOUNT = 0.45; // world units, sideways pan
const GRID_DEPTH_SWAY_AMOUNT = 0.6; // world units, toward/away from camera (dolly)

// Per-tile depth wave - "un effet de vagues en perspective, similaire a
// celui de neongrid": each tile bobs toward/away from the camera along
// a traveling wave sampled at its own world position (same idea as
// neon-grid.js's per-dot pos.z += sin(...) bob, just keyed off raw
// position.x/y here since these tiles don't have neon-grid's aCol/aRow
// attributes). This is a different kind of "3D" from the embossed-bump
// look this file spent a while eliminating earlier (that was a FRAGMENT-
// level per-tile brightness falloff reading as a lit bump on an
// otherwise-static tile); this is real, deliberate whole-grid depth
// motion at the VERTEX level, requested specifically. Kept modest
// relative to SQUARE_SPACING/POINT_WORLD_SIZE so the perspective size
// change this produces stays subtle and can't make neighboring tiles
// visually touch.
// Beat wave - see the note above the tempo constants. The front leaves a new
// origin on every beat (BEAT_ORIGIN_STEP walks it around a circle by the
// golden angle, so consecutive drops never land next to each other) and
// travels far enough to cross the grid's own half-diagonal in one beat, so
// the wave is always finishing roughly as the next one starts.
//
// The crest both BRIGHTENS a tile and kicks it toward the camera: brightness
// alone reads as a moving highlight on a static grid, while the depth kick is
// what makes the floor itself look struck. The depth amount is deliberately
// under DEPTH_WAVE_AMOUNT below - the two stack, and the file's standing rule
// is that tiles must never grow or slide into their neighbours.
// 1/world-units; higher = a narrower crest. At 1.5 the crest's 1/e half-width
// was ~0.67 world units - about HALF one SQUARE_SPACING, so only a single ring
// of tiles ever rode it and the wave changed under 10% of the frame. Widened
// to roughly a two-tile band, which is what makes it read as a front with a
// thickness rather than a thin travelling outline.
const BEAT_WAVE_TIGHTNESS = 1.05;
const BEAT_WAVE_BRIGHTNESS = 0.85;  // added to a tile's own 0..1 field brightness
const BEAT_WAVE_DEPTH = 0.35;       // world units toward the camera at the crest
const BEAT_WAVE_DECAY = 1.7;        // per second - a front is spent before it can pile up
const BEAT_WAVE_BEATS_TO_CROSS = 1; // beats the front takes to cover the half-diagonal
const BEAT_ORIGIN_STEP = 2.39996;   // golden angle in radians
const BEAT_ORIGIN_RADIUS = 0.42;    // fraction of the grid's half-size

const DEPTH_WAVE_AMOUNT = 0.4;
const DEPTH_WAVE_FREQ_X = 0.25;
const DEPTH_WAVE_FREQ_Y = 0.18;
const DEPTH_WAVE_SPEED = 1.0;

const vertexShader = `
  uniform float uTime;
  uniform float uSizeScale;
  uniform vec2 uBeatOrigin;
  uniform float uBeatRadius;
  uniform float uBeatForce;
  attribute vec3 aColor;
  attribute float aTextureSeed;
  varying float vBrightness;
  varying vec3 vColor;
  varying float vTextureSeed;

  void main() {
    vTextureSeed = aTextureSeed;
    // Sampled at this tile's own world position - see the file-level
    // comment above for why this (not a per-tile timer) is what makes lit
    // tiles cluster into shared, moving blobs instead of independent
    // twinkling.
    float field = sin(position.x * ${FIELD_FREQ_X.toFixed(3)} + uTime * ${FIELD_SPEED_X.toFixed(3)})
                 * cos(position.y * ${FIELD_FREQ_Y.toFixed(3)} - uTime * ${FIELD_SPEED_Y.toFixed(3)})
                 + sin((position.x - position.y) * ${FIELD_FREQ_DIAG.toFixed(3)} + uTime * ${FIELD_SPEED_DIAG.toFixed(3)}) * 0.6;
    field /= 1.6;
    vBrightness = smoothstep(${FIELD_LOW.toFixed(3)}, ${FIELD_HIGH.toFixed(3)}, field);

    // The beat front, evaluated at THIS tile's own distance from the drop
    // point - which is the whole reason this is a wave and not a strobe.
    float beatDist = distance(position.xy, uBeatOrigin);
    float crest = exp(-abs(beatDist - uBeatRadius) * ${BEAT_WAVE_TIGHTNESS.toFixed(3)}) * uBeatForce;
    // Clamped: vBrightness drives alpha as well as colour further down, and
    // letting it past 1 would make the crest tiles opaque squares.
    vBrightness = min(vBrightness + crest * ${BEAT_WAVE_BRIGHTNESS.toFixed(3)}, 1.0);
    vColor = aColor;

    // Wave-in-perspective depth bob - see DEPTH_WAVE_* above. x/y stay
    // exactly on-grid always; only z (toward/away from camera) moves.
    vec3 pos = position;
    pos.z += sin(uTime * ${DEPTH_WAVE_SPEED.toFixed(3)} + position.x * ${DEPTH_WAVE_FREQ_X.toFixed(3)} + position.y * ${DEPTH_WAVE_FREQ_Y.toFixed(3)}) * ${DEPTH_WAVE_AMOUNT.toFixed(3)};
    pos.z += crest * ${BEAT_WAVE_DEPTH.toFixed(3)};

    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    // Fixed size always - only brightness animates, never the size, so
    // tiles stay put and can never grow into their neighbors.
    gl_PointSize = ${POINT_WORLD_SIZE.toFixed(3)} * uSizeScale / -mvPosition.z;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const fragmentShader = `
  uniform float uBaseDim;
  uniform float uIntensity;
  uniform float uTime;
  varying float vBrightness;
  varying vec3 vColor;
  varying float vTextureSeed;

  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }

  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = max(abs(uv.x), abs(uv.y)) * 2.0; // 0 at center, 1 at square edge
    if (d > 1.0) discard;
    // Was a much wider soft band (0.68-1.0, then 0.45-1.0 before that) -
    // that alpha falloff, even with a perfectly flat color underneath,
    // faded brightness toward the edges; combined with additive blending
    // and bloom, that radial-ish falloff is exactly what read as a
    // "3D"/embossed bump - bloom blurs a bright center surrounded by a
    // dimming ring into something that looks like a lit sphere, not a
    // flat tile. A near-hard edge (this thin an antialiasing band only)
    // keeps the tile's alpha essentially uniform across its whole
    // interior, which is what actually reads as flat.
    float edge = 1.0 - smoothstep(0.96, 1.0, d);

    // Per-pixel random grain, not a smooth gradient - an earlier version
    // animated a smooth internal light/dark gradient here and it read as
    // a faint embossed/beveled 3D look (any directional brightness
    // gradient across a flat shape mimics a light source hitting a raised
    // surface). Noise has no coherent direction, so it reads as a
    // textured/sparkly surface instead of a bump. Phase-offset per tile
    // (vTextureSeed) and slowly animated (uTime) so it shimmers rather
    // than sitting static.
    float grain = hash12(gl_PointCoord * ${TEXTURE_SCALE.toFixed(3)} + vTextureSeed * 97.0 + uTime * ${TEXTURE_SPEED.toFixed(3)});
    float texturedColor = 1.0 + (grain - 0.5) * 2.0 * ${TEXTURE_AMOUNT.toFixed(3)};

    float brightness = (uBaseDim + vBrightness * uIntensity) * texturedColor;
    gl_FragColor = vec4(vColor * brightness, edge * (uBaseDim * 0.6 + vBrightness));
  }
`;

export class DiscoSquaresVisualizer extends Visualizer {
  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    camera.position.set(0, GRID_CENTER_Y + 0.4, CAMERA_DISTANCE);
    camera.lookAt(0, GRID_CENTER_Y, 0);

    const vFovRad = THREE.MathUtils.degToRad(camera.fov);
    const halfHeight = CAMERA_DISTANCE * Math.tan(vFovRad / 2);
    const halfWidth = halfHeight * camera.aspect;
    const visibleWidth = halfWidth * 2 * COVERAGE;
    const visibleHeight = halfHeight * 2 * COVERAGE;
    const cols = Math.max(2, Math.round(visibleWidth / SQUARE_SPACING) + 1);
    const rows = Math.max(2, Math.round(visibleHeight / SQUARE_SPACING) + 1);
    const gridWidth = (cols - 1) * SQUARE_SPACING;
    const gridHeight = (rows - 1) * SQUARE_SPACING;
    // Half-DIAGONAL, not half-width: a front dropped off-centre still has to
    // reach the far corner within its beat, or the wave visibly dies mid-grid.
    this._gridHalfSpan = Math.sqrt(gridWidth * gridWidth + gridHeight * gridHeight) / 2;
    this._gridHalfSize = Math.min(gridWidth, gridHeight) / 2;

    const count = cols * rows;
    this._count = count;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const textureSeeds = new Float32Array(count);
    this._baseHue = new Float32Array(count);
    this._baseSat = new Float32Array(count);
    this._baseLight = new Float32Array(count);
    this._vividSlot = new Uint8Array(count);
    this._jitterSeed = new Float32Array(count);
    this._usesAlbumColor = new Uint8Array(count);
    const tmpColor = new THREE.Color();

    let i = 0;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        // Centered on the object's own local origin (no +GRID_CENTER_Y
        // here) - GRID_CENTER_Y is applied once as this._points.position.y
        // below instead, so a rotation on the object pivots around the
        // grid's actual visual center rather than swinging around a point
        // far outside the visible area.
        positions[i * 3] = col * SQUARE_SPACING - gridWidth / 2;
        positions[i * 3 + 1] = row * SQUARE_SPACING - gridHeight / 2;
        positions[i * 3 + 2] = 0;
        textureSeeds[i] = Math.random();

        const anchor = HUE_ANCHORS[Math.floor(Math.random() * HUE_ANCHORS.length)];
        const hue = (anchor + (Math.random() * 2 - 1) * HUE_JITTER + 1) % 1;
        const sat = SAT_MIN + Math.random() * (SAT_MAX - SAT_MIN);
        const light = LIGHT_MIN + Math.random() * (LIGHT_MAX - LIGHT_MIN);
        this._baseHue[i] = hue;
        this._baseSat[i] = sat;
        this._baseLight[i] = light;
        this._vividSlot[i] = Math.floor(Math.random() * 5);
        this._jitterSeed[i] = Math.random();
        this._usesAlbumColor[i] = Math.random() < 0.5 ? 1 : 0;
        tmpColor.setHSL(hue, sat, light);
        colors[i * 3] = tmpColor.r;
        colors[i * 3 + 1] = tmpColor.g;
        colors[i * 3 + 2] = tmpColor.b;

        i++;
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('aTextureSeed', new THREE.BufferAttribute(textureSeeds, 1));

    this._material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uSizeScale: { value: 1 },
        uBeatOrigin: { value: new THREE.Vector2(0, 0) },
        uBeatRadius: { value: 0 },
        uBeatForce: { value: 0 },
        uBaseDim: { value: BASE_DIM },
        uIntensity: { value: INTENSITY_BOOST }
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });

    this._points = new THREE.Points(geometry, this._material);
    this._points.position.y = GRID_CENTER_Y;
    scene.add(this._points);

    this._time = 0;
    // Separate from this._time (which is tempo-scaled - see update()) so
    // the grid's own glide stays a constant, gentle ambient motion
    // regardless of the track's BPM, the same way scene-manager.js's own
    // camera drift is independent of whatever content is on screen.
    this._driftTime = 0;
    this._lastPaletteRef = null;
    // The beat front's own clock is _driftTime (real seconds, pause-aware),
    // NOT _time - _time is tempo-scaled, and a front whose speed is already
    // derived from the BPM must not be scaled by the tempo a second time.
    this._beatOriginAngle = 0;
    this._waveBornAt = -999;
    this._lastBeatIndex = null;
  }

  // Mirrors SpotWall's _recomputeColors exactly - see its comment there
  // for the reasoning behind the 50/50 split and per-spot jitter.
  _recomputeColors(profile) {
    const vivid = profile.artworkPalette && profile.artworkPalette.vividColors;
    const hasVivid = vivid && vivid.length > 0;
    const tmpColor = new THREE.Color();
    const hsl = { h: 0, s: 0, l: 0 };
    const colorAttr = this._points.geometry.attributes.aColor;
    for (let i = 0; i < this._count; i++) {
      if (hasVivid && this._usesAlbumColor[i]) {
        tmpColor.set(vivid[this._vividSlot[i] % vivid.length]);
        tmpColor.getHSL(hsl);
        const jitter = (this._jitterSeed[i] - 0.5) * 2 * VIVID_JITTER_AMOUNT;
        const sat = THREE.MathUtils.clamp(hsl.s * (1 + jitter), 0, 1);
        const light = THREE.MathUtils.clamp(hsl.l * (1 + jitter * 0.6), 0.12, 0.88);
        tmpColor.setHSL(hsl.h, sat, light);
      } else {
        tmpColor.setHSL(this._baseHue[i], this._baseSat[i], this._baseLight[i]);
      }
      colorAttr.array[i * 3] = tmpColor.r;
      colorAttr.array[i * 3 + 1] = tmpColor.g;
      colorAttr.array[i * 3 + 2] = tmpColor.b;
    }
    colorAttr.needsUpdate = true;
  }

  // One front at a time, described to the shader as a radius and a force.
  // beatIndex is an absolute whole-beat count (see scene-manager.js), so a
  // beat cannot be swallowed by a slow frame the way a rising-edge test on
  // beatPhase would swallow it.
  _updateBeatWave(profile, bpm) {
    const paused = profile.playbackState !== 'PLAYING';
    const uniforms = this._material.uniforms;

    if (!paused && profile.beatIndex != null) {
      if (profile.beatIndex !== this._lastBeatIndex) {
        this._lastBeatIndex = profile.beatIndex;
        this._waveBornAt = this._driftTime;
        this._beatOriginAngle += BEAT_ORIGIN_STEP;
        const r = this._gridHalfSize * BEAT_ORIGIN_RADIUS;
        uniforms.uBeatOrigin.value.set(
          Math.cos(this._beatOriginAngle) * r,
          Math.sin(this._beatOriginAngle) * r
        );
      }
    } else {
      this._lastBeatIndex = null;
    }

    const age = this._driftTime - this._waveBornAt;
    if (age < 0 || age > 8) {
      uniforms.uBeatForce.value = 0;
      return;
    }
    const beatPeriod = 60 / (bpm || REFERENCE_BPM);
    uniforms.uBeatRadius.value =
      (age * this._gridHalfSpan) / (beatPeriod * BEAT_WAVE_BEATS_TO_CROSS);
    uniforms.uBeatForce.value = Math.exp(-age * BEAT_WAVE_DECAY);
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    const bpm = profile.trackFeatures && profile.trackFeatures.bpm;
    const tempoMul = bpm
      ? THREE.MathUtils.clamp(bpm / REFERENCE_BPM, TEMPO_MULTIPLIER_MIN, TEMPO_MULTIPLIER_MAX)
      : 1;
    if (!paused) this._time += deltaTime * tempoMul;
    this._material.uniforms.uTime.value = this._time;

    // Slow, gentle glide - the grid itself pans/dollies a little instead
    // of the camera (see the GRID_SWAY_AMOUNT/GRID_DEPTH_SWAY_AMOUNT
    // comment above for why translation only), giving the same
    // "perspective sliding" feel while keeping every tile exactly on-grid.
    if (!paused) this._driftTime += deltaTime;
    this._points.position.x = Math.sin(this._driftTime * 0.1 + 0.6) * GRID_SWAY_AMOUNT;
    this._points.position.z = Math.sin(this._driftTime * 0.07 + 2.3) * GRID_DEPTH_SWAY_AMOUNT;

    this._updateBeatWave(profile, bpm);

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._recomputeColors(profile);
    }

    const canvasPixelHeight = window.innerHeight * Math.min(window.devicePixelRatio || 1, 2);
    const fovRadians = THREE.MathUtils.degToRad(this._camera.fov);
    this._material.uniforms.uSizeScale.value = canvasPixelHeight / (2 * Math.tan(fovRadians / 2));

    const intensity = profile.director.intensity;
    this._material.uniforms.uIntensity.value = INTENSITY_BOOST * (0.75 + intensity * 0.5);
  }

  dispose() {
    this._scene.remove(this._points);
    this._points.geometry.dispose();
    this._material.dispose();
  }
}
